import { useCallback, useEffect, useState } from "react";
import { ThumbsDown, ThumbsUp } from "@phosphor-icons/react";
import { ShimmerText } from "~/components/assistant/ShimmerText";
import {
  NEGATIVE_IMPACT_LABELS,
  POSITIVE_IMPACT_LABELS,
} from "~/components/assistant/impact-options";
import { cn } from "~/lib/utils";
import { AgentAvatar } from "~/pages/manifold-nextgen/components/agents/AgentAvatar";
import type { PartialAgent } from "~/services/agents";
import {
  deleteTurnFeedback,
  putTurnFeedback,
  type FeedbackCreate,
  type TurnFeedback as TurnFeedbackValue,
} from "~/services/turns";
import { Markdown } from "./Markdown";
import { ToolCallsDisclosure } from "./ToolCallsDisclosure";
import { TurnFeedback } from "./TurnFeedback";
import type { AssistantTurnData } from "./useTurns";

interface AssistantTurnProps {
  turn: AssistantTurnData;
  agent: PartialAgent | null;
  conversationId: string;
  // True between the user clicking Stop and the run ending. Only meaningful
  // on the in-flight turn (the one with `turn.isRunning`); changes the
  // status label from "Thinking" to "Stopping" so the chat area itself
  // reflects the pending cancel.
  isCancelling?: boolean;
  // Admin-review mode: hide the interactive thumbs-up/down bar and render
  // a static, tinted feedback card with a thread line down from the
  // avatar so positive/negative turns stand out at a glance. No-op if the
  // turn has no persisted feedback.
  readOnlyFeedback?: boolean;
}

export const AssistantTurn = ({
  turn,
  agent,
  conversationId,
  isCancelling,
  readOnlyFeedback = false,
}: AssistantTurnProps) => {
  const showThinking = turn.isRunning;
  const hasTools = turn.toolCalls.length > 0;
  const hasText = turn.text.length > 0;
  const showCancelled = turn.cancelled && !turn.isRunning;
  const showInteractiveFeedback =
    !readOnlyFeedback && !turn.isRunning && !turn.cancelled && !!turn.turnId;
  const adminFeedback = readOnlyFeedback ? binaryFeedback(turn) : null;

  const [feedback, setFeedback] = useState<TurnFeedbackValue | FeedbackCreate | null>(
    turn.feedback ?? null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  // True iff we watched this turn stream to completion in the current
  // session. Turns loaded from /connect history mount with isRunning=false
  // and stay that way — `sawRunning` never flips and the feedback bar
  // appears without animation. Live turns mount with isRunning=true, the
  // effect flips `sawRunning` on the next tick, and when isRunning later
  // becomes false the feedback bar renders with the entrance animation.
  const [sawRunning, setSawRunning] = useState(false);
  useEffect(() => {
    if (turn.isRunning) setSawRunning(true);
  }, [turn.isRunning]);

  // Re-sync if /connect later returns a different feedback value for this
  // turn (e.g. the conversation reloaded after a navigation).
  useEffect(() => {
    setFeedback(turn.feedback ?? null);
  }, [turn.feedback]);

  const handleSubmit = useCallback(
    async (value: 0 | 1, text: string | null, impact: number | null) => {
      if (!turn.turnId || isSubmitting) return;
      setIsSubmitting(true);
      try {
        const payload: FeedbackCreate = {
          feedback_type: "BINARY",
          feedback_value: value,
          text,
          impact,
        };
        const result = await putTurnFeedback(conversationId, turn.turnId, payload);
        setFeedback(result);
      } catch (err) {
        console.error("Failed to submit turn feedback", err);
      } finally {
        setIsSubmitting(false);
      }
    },
    [conversationId, isSubmitting, turn.turnId]
  );

  const handleClear = useCallback(async () => {
    if (!turn.turnId || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await deleteTurnFeedback(conversationId, turn.turnId);
      setFeedback(null);
    } catch (err) {
      console.error("Failed to clear turn feedback", err);
    } finally {
      setIsSubmitting(false);
    }
  }, [conversationId, isSubmitting, turn.turnId]);

  return (
    <li className="flex w-full items-stretch gap-3 self-start">
      <div className="flex w-7 flex-col items-center">
        <AgentAvatar id={agent?.id} name={agent?.name} size={28} aria-hidden="true" />
        {adminFeedback && (
          <div
            className={cn(
              "mt-1 w-0.5 flex-1 rounded-full",
              adminFeedback.tone === "positive" ? "bg-emerald-500/60" : "bg-destructive/60"
            )}
          />
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        {showThinking && (
          <div className="inline-flex items-center text-xs" aria-live="polite">
            <ShimmerText>{isCancelling ? "Stopping" : "Thinking"}</ShimmerText>
          </div>
        )}
        {hasTools && <ToolCallsDisclosure entries={turn.toolCalls} />}
        {hasText && (
          <div className="px-1 py-1 text-sm text-foreground">
            <Markdown text={turn.text} />
          </div>
        )}
        {showCancelled && (
          <div className="px-1 text-xs italic text-muted-foreground" aria-live="polite">
            Response cancelled
          </div>
        )}
        {showInteractiveFeedback && (
          <div
            className={
              sawRunning
                ? "-ml-1 mt-0.5 animate-in fade-in slide-in-from-bottom-1 fill-mode-both duration-300 delay-150 ease-out"
                : "-ml-1 mt-0.5"
            }
          >
            <TurnFeedback
              feedback={feedback}
              isSubmitting={isSubmitting}
              onSubmit={handleSubmit}
              onClear={handleClear}
            />
          </div>
        )}
        {adminFeedback && <FeedbackCard feedback={adminFeedback} />}
      </div>
    </li>
  );
};

interface BinaryFeedback {
  tone: "positive" | "negative";
  impactLabel: string | null;
  text: string | null;
}

function binaryFeedback(turn: AssistantTurnData): BinaryFeedback | null {
  const fb = turn.feedback;
  if (!fb || fb.feedback_type !== "BINARY" || fb.feedback_value == null) return null;
  const isPositive = fb.feedback_value === 1;
  return {
    tone: isPositive ? "positive" : "negative",
    impactLabel: impactLabel(fb, isPositive),
    text: fb.text?.trim() || null,
  };
}

function impactLabel(fb: TurnFeedbackValue, isPositive: boolean): string | null {
  if (fb.impact == null) return null;
  const table = isPositive ? POSITIVE_IMPACT_LABELS : NEGATIVE_IMPACT_LABELS;
  return table[fb.impact] ?? null;
}

const FeedbackCard = ({ feedback }: { feedback: BinaryFeedback }) => {
  const isPositive = feedback.tone === "positive";
  return (
    <div
      className={cn(
        "flex flex-col gap-1 rounded-md border px-3 py-2 text-xs",
        isPositive
          ? "border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-800"
          : "border-destructive/30 bg-destructive/[0.06] text-destructive"
      )}
    >
      <div className="flex items-center gap-1.5">
        {isPositive ? <ThumbsUp size={14} weight="fill" /> : <ThumbsDown size={14} weight="fill" />}
        <span className="font-medium">User feedback</span>
        {feedback.impactLabel && (
          <span className="font-normal opacity-80">· {feedback.impactLabel}</span>
        )}
      </div>
      {feedback.text && <div className="italic">&ldquo;{feedback.text}&rdquo;</div>}
    </div>
  );
};
